<template lang="pug">
  SupplierLayout
    CommonSection.b-supplier-requests-page(title="Запросы КП" data-testid="supplier-requests-page")
      ._bar
        button._sort(type="button" data-testid="supplier-requests-sort-date" @click="toggleSort")
          | по дате {{ desc ? '↓' : '↑' }}
      ._empty(v-if="!loading && !sorted.length" data-testid="supplier-requests-empty") Запросов пока нет
      ._list
        ._item(v-for="request in sorted" :key="request.id" data-testid="supplier-requests-item")
          ._main
            router-link._open(
              :to="{ name: 'supplier-request', params: { requestId: String(request.id) } }"
              data-testid="supplier-requests-item-open"
              ) Запрос КП № {{ request.id }}
            ._line
              span._designer(data-testid="supplier-requests-item-designer") {{ request.designerName || 'Дизайнер' }}
              span._date(data-testid="supplier-requests-item-date") {{ date(request.createdAt) }}
              span._status(data-testid="supplier-requests-item-status") {{ requestStatusTitle(request.status) }}
            ._line
              span._count(data-testid="supplier-requests-item-items-count") позиций: {{ request.itemsCount }}
              span._count(data-testid="supplier-requests-item-offers-count") откликов отправлено: {{ request.offersCount }}
          ._marks
            span._mark.rework(v-if="request.reworkCount > 0" data-testid="supplier-requests-item-rework")
              | есть на доработке ({{ request.reworkCount }})
            span._mark.attention(
              v-if="request.hasConfirmationPending"
              data-testid="supplier-requests-item-confirmation"
              ) требуется подтверждение
            span._mark.chosen(v-if="request.hasChosenOffers" data-testid="supplier-requests-item-chosen") ваш отклик выбран
            span._mark(v-if="request.hasReRequested" data-testid="supplier-requests-item-rerequested") повторный запрос
            span._mark(v-if="request.readOnly" data-testid="supplier-requests-item-readonly") только чтение
</template>

<script>
import SupplierLayout from '@/layouts/Supplier.vue';
import CommonSection from '@/components/CommonSection.vue';
import model from '@/model/supplierCabinet';
import { date, requestStatusTitle } from '@/utils/spec';

/**
 * Список запросов КП в кабинете поставщика (ТЗ 6.5): дата, имя дизайнера, статус, число позиций,
 * число отправленных откликов, метки «есть на доработке», «требуется подтверждение», «ваш отклик
 * выбран», «повторный запрос» и «только чтение»; сортировка по дате.
 *
 * Все вычисляемые поля запроса приходят с сервера (ТЗ 4.2), здесь только показ и сортировка.
 * Название проекта и заказчик в срез поставщика не входят (ТЗ 3.4). Сгенерировано Claude.
 */
export default {
  name: 'SupplierRequestsPage',
  components: { SupplierLayout, CommonSection },
  data() {
    return {
      requests: [],
      desc: true,
      loading: true,
    };
  },
  computed: {
    sorted() {
      const list = [...this.requests];
      list.sort((a, b) => {
        const diff = (Date.parse(a.createdAt ?? '') || 0) - (Date.parse(b.createdAt ?? '') || 0);
        return this.desc ? -diff : diff;
      });
      return list;
    },
  },
  created() {
    this.load();
  },
  methods: {
    date,
    requestStatusTitle,
    async load() {
      this.requests = await model.fetchRequests();
      this.loading = false;
    },
    toggleSort() {
      this.desc = !this.desc;
    },
  },
};
</script>

<style scoped lang="scss">
.supplier-requests-page {
  &__bar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
  }

  &__sort {
    border: none;
    background: none;
    padding: 0;
    font: inherit;
    font-size: 12px;
    color: var(--color-accent);
    cursor: pointer;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    padding: 14px 16px;
    border: 1px solid var(--color-super-light);
    border-radius: 8px;
    background: var(--color-white);
  }

  &__main {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__open {
    font-weight: 700;
    color: var(--color-accent);
  }

  &__line {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 16px;
    font-size: 13px;
  }

  &__status {
    font-weight: 700;
  }

  &__count,
  &__date {
    color: var(--color-middle-gray);
  }

  &__marks {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
  }

  &__mark {
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 10px;
    background: #F1F5FA;
    color: var(--color-middle-gray);

    &.rework { color: var(--color-orange); }
    &.attention { color: var(--color-red); }
    &.chosen { color: var(--color-green); }
  }

  &__empty {
    font-size: 14px;
    color: var(--color-middle-gray);
  }

  // Узкие экраны (общий порог модуля — 768 px): строка запроса складывается в одну колонку,
  // метки встают в ряд под ней, а не сжимают основную часть
  @media (max-width: 768px) {
    &__item {
      flex-direction: column;
      gap: 10px;
    }

    &__marks {
      flex-direction: row;
      flex-wrap: wrap;
      align-items: flex-start;
    }
  }
}
</style>
